import client from "../client";
import { QueryFunctionContext } from "@tanstack/react-query";
import appendQueryParams from "@/utils/appendQueryParams";

export function createCreative({
  payload,
  type,
}: {
  payload: any;
  type: string;
}) {
  return client.post(`/creatives/${type}`, payload);
}
export function editCreative({
  payload,
  type,
  id,
}: {
  payload: any;
  type: string;
  id: number;
}) {
  return client.put(`/creatives/${type}/${id}`, payload);
}
export function getAssets(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/creatives`, data.meta?.queryParams ?? {})
  );
}
export function getVisitorInsightAudience(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `/audiences/visitor-insights`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function getAssetDetails(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `/creatives/${data.meta?.type}/${data.meta?.id}/lineitems`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function refreshAds({
  id,
  type,
  queryParams,
}: {
  id: number;
  type: string;
  queryParams?: any;
}) {
  return client.put(
    appendQueryParams(`/creatives/${type}/${id}/refresh`, queryParams ?? {})
  );
}
export function deleteAd({
  id,
  type,
  queryParams,
}: {
  id: number;
  type: string;
  queryParams?: any;
}) {
  return client.delete(
    appendQueryParams(`/creatives/${type}/${id}`, queryParams ?? {})
  );
}
export function createAssets({ type, data }: { type: string; data: any }) {
  return client.post(`/creatives/${type}/assets`, data);
}

// Bulk tag-sheet upload. Unlike createCreative this is one request for the whole
// upload: the backend creates every creative and answers with a per-row manifest.
export function createBulkTags({ data }: { data: FormData }) {
  return client.post(`/creatives/display/bulk-tags`, data);
}

export function getCreativeAddons(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `creatives/display/creative_addon`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function getCreativeApprovals(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `creatives/display/approval/status`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function getImageByUrn(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `creatives/linkedin/assets/${data?.meta?.id}`,
      data.meta?.queryParams ?? {}
    )
  );
}

export function getBwCreativeDetails(data: QueryFunctionContext) {
  return client.get(`creatives/display/details/${data?.meta?.id}`);
}
export function getBwCreativeAsset(data: QueryFunctionContext) {
  return client.get(`creatives/display/asset/${data?.meta?.id}`);
}

/**
 * Update one field on many creatives in a single request — what the list's edit
 * mode saves through.
 *
 * A PATCH with a partial body per row, NOT `editCreative`: that is a PUT taking
 * the whole creative, and rebuilding a full payload per row client-side risks
 * dropping any field the list's row data doesn't carry. `updateCreativesByGroupAdId`
 * below is the precedent for the backend merging a partial body.
 *
 * Answers per id rather than with one status, because a partial failure is the
 * normal case and the UI has to say WHICH row was refused:
 *
 *     { updated: [101, 102], failed: [{ id: 103, error: "…" }] }
 *
 * A response without `failed` is taken as everything having saved.
 *
 * Beeswax only, and the `display` in the path is why — not a channel-agnostic
 * `/creatives/bulk`. A row's `id` is its CHANNEL table's id, and DisplayAds,
 * LinkedinAds and FacebookAds have independent id sequences, so an unscoped
 * endpoint could not tell which table an id names and would happily update
 * another advertiser's creative. Every write in that module is channel-scoped
 * for the same reason. A LinkedIn click URL is a different call to a different
 * vendor; when it ships, this takes a channel and the caller passes it.
 */
export function bulkUpdateCreatives({
  payload,
}: {
  payload: Array<{ id: number } & Record<string, unknown>>;
}) {
  return client.patch(`/creatives/display/bulk`, payload);
}

export function updateCreativesByGroupAdId({
  payload,
  groupAdId,
}: {
  payload: any;
  groupAdId: string;
}) {
  return client.put(`/creatives/group/${groupAdId}`, payload);
}
